import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ShieldCheck } from 'lucide-react';
import { PinPad } from '../components/common/PinPad';

const Ctx = createContext(null);

// Il PIN di sicurezza è unico per attività. Lo teniamo in memoria di sessione
// così l'operatore lo inserisce una sola volta per turno.
const SS_KEY = 'rmp_sec_pin';
const getCachedPin = () => { try { return sessionStorage.getItem(SS_KEY) || null; } catch { return null; } };
const setCachedPin = (p) => { try { sessionStorage.setItem(SS_KEY, p); } catch { /* noop */ } };
const clearCachedPin = () => { try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ } };

export const SecurityPinProvider = ({ children }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('Inserisci il PIN del profilo');
  const resolversRef = useRef([]);

  const closeAndReject = useCallback((reason) => {
    setOpen(false);
    setPin('');
    const list = resolversRef.current; resolversRef.current = [];
    list.forEach((r) => r.reject(new Error(reason || 'cancelled')));
  }, []);

  // Apre il dialog PIN e risolve con il PIN verificato (o rigetta se annullato).
  // Gestisce richieste concorrenti: tutte condividono lo stesso prompt.
  const requestPin = useCallback(({ titleText } = {}) => new Promise((resolve, reject) => {
    resolversRef.current.push({ resolve, reject });
    setTitle(titleText || 'Inserisci il PIN del profilo');
    setError('');
    setOpen((wasOpen) => { if (!wasOpen) setPin(''); return true; });
  }), []);

  const submit = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await api.post('/security/verify-pin', { pin });
      setCachedPin(pin);
      setOpen(false);
      const list = resolversRef.current; resolversRef.current = [];
      const value = pin; setPin('');
      list.forEach((r) => r.resolve(value));
    } catch (e) {
      setError(e?.response?.data?.detail || 'PIN non valido');
      setPin('');
    } finally { setBusy(false); }
  }, [busy, pin]);

  /**
   * Esegue una chiamata sensibile gestendo automaticamente il PIN.
   * - Admin senza PIN: la prima chiamata va a buon fine.
   * - Manager: alla 428 (PIN richiesto) apre il dialog e ripete con header.
   * - PIN in cache errato/scaduto (403): ripulisce e richiede di nuovo.
   * cb riceve un oggetto config axios da passare alla chiamata.
   */
  const runSensitive = useCallback(async (cb, opts = {}) => {
    const cached = getCachedPin();
    try {
      return await cb(cached ? { headers: { 'X-Action-Pin': cached } } : {});
    } catch (e) {
      const st = e?.response?.status;
      if (st === 428 || (st === 403 && cached)) {
        clearCachedPin();
        const p = await requestPin({ titleText: opts.titleText });
        return cb({ headers: { 'X-Action-Pin': p } });
      }
      throw e;
    }
  }, [requestPin]);

  const canSubmit = pin.length >= 4 && !busy;

  return (
    <Ctx.Provider value={{ requestPin, runSensitive, clearCachedPin, role: user?.role }}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndReject('cancelled'); }}>
        <DialogContent className="sm:max-w-sm" data-testid="security-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> {title}
            </DialogTitle>
            <DialogDescription>Operazione protetta: digita il PIN del tuo profilo (lo stesso dell'accesso).</DialogDescription>
          </DialogHeader>

          {/* Tastierino grande (stesso della schermata di accesso) */}
          <div className="mt-2">
            <PinPad
              value={pin}
              onChange={(next) => { setError(''); setPin(next); }}
              length={6}
              disabled={busy}
              onComplete={() => { submit(); }}
            />
          </div>

          {error ? <div className="text-sm text-destructive text-center mt-1" data-testid="security-pin-error">{error}</div> : null}

          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => closeAndReject('cancelled')} data-testid="security-pin-cancel">Annulla</Button>
            <Button className="flex-1 rounded-xl" disabled={!canSubmit} onClick={submit} data-testid="security-pin-submit">
              {busy ? 'Verifica…' : 'Conferma'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
};

export const useSecurityPin = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSecurityPin deve essere usato dentro SecurityPinProvider');
  return ctx;
};
